import type { CacheListItemData } from '../store/slices/userProductsSlice';
import { itemDisplayValue } from '../lib/itemValuation';

export interface InventoryDuplicateGroup {
  itemIds: string[];
  confidence: number;
  reason: string;
  representativeItem: CacheListItemData;
}

/**
 * Detects duplicate products in the user's saved inventory
 */
export function detectInventoryDuplicates(items: CacheListItemData[]): InventoryDuplicateGroup[] {
  const groups: InventoryDuplicateGroup[] = [];
  const processed = new Set<string>();

  for (let i = 0; i < items.length; i++) {
    if (processed.has(items[i].id)) continue;

    const currentItem = items[i];
    const similarIds = [currentItem.id];
    processed.add(currentItem.id);

    // Look for similar items
    for (let j = i + 1; j < items.length; j++) {
      if (processed.has(items[j].id)) continue;

      const otherItem = items[j];
      const similarity = calculateInventorySimilarity(currentItem, otherItem);

      // If similarity is high enough, group them (threshold: 0.85)
      if (similarity.score >= 0.85) {
        similarIds.push(otherItem.id);
        processed.add(otherItem.id);
      }
    }

    // Only create a group if we found duplicates
    if (similarIds.length > 1) {
      const avgConfidence = similarIds.reduce((sum, id) => {
        const item = items.find(i => i.id === id);
        return sum + (item?.confidenceScore || 0);
      }, 0) / similarIds.length;

      const secondItem = items.find(i => i.id === similarIds[1]);
      
      groups.push({
        itemIds: similarIds,
        confidence: avgConfidence,
        reason: getSimilarityReason(currentItem, secondItem!),
        representativeItem: currentItem,
      });
    }
  }

  return groups;
}

interface SimilarityResult {
  score: number;
  reasons: string[];
}

/**
 * Calculates similarity score between two inventory items (0-1)
 */
function calculateInventorySimilarity(item1: CacheListItemData, item2: CacheListItemData): SimilarityResult {
  let score = 0;
  const reasons: string[] = [];

  // Brand matching (most important)
  const brand1 = normalizeString(item1.brandName || '');
  const brand2 = normalizeString(item2.brandName || '');
  const sameBrand = brand1 && brand2 && brand1 === brand2;
  const oneBrandUnknown = brand1 === 'unknown' || brand2 === 'unknown';
  const bothBrandsUnknown = brand1 === 'unknown' && brand2 === 'unknown';

  // IMPORTANT: If either brand is unknown, items cannot be duplicates
  // Unknown brands represent completely different product types
  if (oneBrandUnknown) {
    return { score: 0, reasons: ['unknown brand - cannot match'] };
  }

  // Model matching
  const model1 = normalizeString(item1.modelName || '');
  const model2 = normalizeString(item2.modelName || '');
  const sameModel = model1 && model2 && model1 === model2;

  // Family matching
  const family1 = normalizeString(item1.familyName || '');
  const family2 = normalizeString(item2.familyName || '');
  const sameFamily = family1 && family2 && family1 === family2;

  // Same brand + same model = very likely duplicate
  if (sameBrand && sameModel) {
    score += 0.7;
    reasons.push('same brand and model');
  }

  // Same brand + same family (but different/missing model)
  if (sameBrand && sameFamily && !sameModel) {
    score += 0.4;
    reasons.push('same brand and family');
  }

  // Condition matching (bonus)
  if (item1.condition && item2.condition && 
      normalizeString(item1.condition) === normalizeString(item2.condition)) {
    score += 0.1;
    reasons.push('same condition');
  }

  // Valuation similarity (bonus) — displayed item values only (V3 P6)
  const val1 = itemDisplayValue(item1);
  const val2 = itemDisplayValue(item2);
  if (val1 && val2) {
    const maxVal = Math.max(val1, val2);
    const minVal = Math.min(val1, val2);
    
    // If valuations are within 20% of each other
    if (maxVal < minVal * 1.2) {
      score += 0.2;
      reasons.push('similar valuation');
    }
  }

  // Variant attributes matching
  if (item1.variantAttributes && item2.variantAttributes) {
    const attrSimilarity = calculateAttributeSimilarity(
      item1.variantAttributes,
      item2.variantAttributes
    );
    if (attrSimilarity > 0.5) {
      score += 0.2 * attrSimilarity;
      reasons.push('similar attributes');
    }
  }

  return { score: Math.min(score, 1), reasons };
}

/**
 * Calculate similarity between variant attributes
 */
function calculateAttributeSimilarity(attr1: Record<string, any>, attr2: Record<string, any>): number {
  const keys1 = Object.keys(attr1);
  const keys2 = Object.keys(attr2);
  
  if (keys1.length === 0 && keys2.length === 0) return 1;
  if (keys1.length === 0 || keys2.length === 0) return 0;
  
  const allKeys = new Set([...keys1, ...keys2]);
  let matches = 0;
  
  allKeys.forEach(key => {
    const val1 = normalizeString(String(attr1[key] || ''));
    const val2 = normalizeString(String(attr2[key] || ''));
    
    if (val1 && val2 && val1 === val2) {
      matches++;
    }
  });
  
  return matches / allKeys.size;
}

/**
 * Get human-readable reason for similarity
 */
function getSimilarityReason(item1: CacheListItemData, item2: CacheListItemData): string {
  const reasons: string[] = [];

  if (item1.brandName && item2.brandName && 
      normalizeString(item1.brandName) === normalizeString(item2.brandName)) {
    reasons.push('same brand');
  }

  if (item1.modelName && item2.modelName) {
    const similarity = calculateStringSimilarity(
      normalizeString(item1.modelName),
      normalizeString(item2.modelName)
    );
    if (similarity > 0.8) {
      reasons.push('same model');
    } else if (similarity > 0.5) {
      reasons.push('similar model');
    }
  }

  if (reasons.length === 0) {
    return 'Similar characteristics';
  }

  return reasons.join(' and ');
}

/**
 * Normalize string for comparison
 */
function normalizeString(str: string): string {
  return str.toLowerCase().trim().replace(/[^a-z0-9]/g, '');
}

/**
 * Calculate string similarity using Levenshtein distance
 */
function calculateStringSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) {
    return 1.0;
  }

  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}
